import { LoadingScreen } from 'src/components/loading-screen';
import {
  Navigate,
  Outlet,
  RouterProvider,
  createBrowserRouter,
} from 'react-router-dom';
import { Suspense, lazy } from 'react';

import AuthClassicLayout from 'src/layouts/auth/classic';
import CompactLayout from 'src/layouts/compact';
import DashboardLayout from 'src/layouts/dashboard';
import { AuthGuard, GuestGuard } from 'src/auth/guard';
import PropertyAssetsPage from 'src/pages/property/property-assets-page';
import FeatureListPage from 'src/pages/features/feature-list-page';
import PropertyRenovationPage from 'src/pages/property/property-renovation-page';
import PropertyCasafariPage from 'src/pages/property/property-casafari-page';
import PropertySimilaritiesPage from 'src/pages/property/property-similarities-page';

const SettingsListPage = lazy(
  () => import('src/pages/settings/settings-list-page')
);
const AuthPage = lazy(() => import('src/pages/auth'));
const AnalyticsPage = lazy(() => import('src/pages/analytics/analytics-page'));
const UserLayout = lazy(() => import('src/modules/users/layouts/user-layout'));
const UserListPage = lazy(() => import('src/pages/users/user-list-page'));
const UserDetailPage = lazy(() => import('src/pages/users/user-detail-page'));
const UserHeatmapPage = lazy(() => import('src/pages/users/user-heatmap-page'));
const WhatsAppPage = lazy(() => import('src/pages/whatsapp/whatsapp-page'));
const LocationPage = lazy(() => import('src/pages/locations/location-page'));
const PropertyListPage = lazy(
  () => import('src/pages/property/property-list-page')
);
const PropertyLayout = lazy(
  () => import('src/modules/properties/layouts/property-layout')
);
const PropertyOverviewPage = lazy(
  () => import('src/pages/property/property-overview-page')
);
const PropertyCreatePage = lazy(
  () => import('src/pages/property/property-create-page')
);
const PropertyEditPage = lazy(
  () => import('src/pages/property/property-edit-page')
);
const OnelinkPage = lazy(() => import('src/pages/onelink/onelink-page'));
// const Costs = lazy(() => import('src/modules/settings/Costs/Costs'));
const Page404 = lazy(() => import('src/pages/404'));

const ChatLayout = lazy(() => import('src/modules/chat/layouts/chat-layout'));
const ChatSystemPromptPage = lazy(
  () => import('src/pages/chat/chat-system-prompt-page')
);
const ChatConversationsPage = lazy(
  () => import('src/pages/chat/chat-conversations-page')
);

const router = createBrowserRouter([
  // Auth routes
  {
    path: 'auth',
    element: (
      <GuestGuard>
        <AuthClassicLayout>
          <AuthPage />
        </AuthClassicLayout>
      </GuestGuard>
    ),
  },

  // Dashboard routes
  {
    path: '/',
    element: (
      <AuthGuard>
        <DashboardLayout>
          <Suspense fallback={<LoadingScreen />}>
            <Outlet />
          </Suspense>
        </DashboardLayout>
      </AuthGuard>
    ),
    children: [
      {
        index: true,
        element: <AnalyticsPage />,
      },
      {
        path: 'users',
        children: [
          { index: true, element: <UserListPage /> },
          {
            path: ':sub',
            element: <UserLayout />,
            children: [
              {
                index: true,
                element: <UserDetailPage />,
              },
              {
                path: 'heatmap',
                element: <UserHeatmapPage />,
              },
            ],
          },
        ],
      },
      {
        path: 'whatsapp',
        element: <WhatsAppPage />,
      },
      {
        path: 'locations',
        element: <LocationPage />,
      },
      {
        path: 'features',
        children: [{ index: true, element: <FeatureListPage /> }],
      },
      {
        path: 'properties',
        children: [
          { index: true, element: <PropertyListPage /> },
          { path: 'add', element: <PropertyCreatePage /> },
          {
            path: ':id',
            element: <PropertyLayout />,
            children: [
              { index: true, element: <PropertyOverviewPage /> },
              { path: 'edit', element: <PropertyEditPage /> },
              { path: 'assets', element: <PropertyAssetsPage /> },
              { path: 'renovation', element: <PropertyRenovationPage /> },
              { path: 'casafari', element: <PropertyCasafariPage /> },
              { path: 'similarities', element: <PropertySimilaritiesPage /> },
            ],
          },
        ],
      },
      {
        path: 'onelink',
        element: <OnelinkPage />,
      },
      {
        path: 'settings',
        children: [{ index: true, element: <SettingsListPage /> }],
      },
      {
        path: 'chat',
        element: <ChatLayout />,
        children: [
          { index: true, element: <ChatConversationsPage /> },
          { path: 'system-prompt', element: <ChatSystemPromptPage /> },
        ],
      },
    ],
  },

  // Main routes
  {
    element: (
      <CompactLayout>
        <Outlet />
      </CompactLayout>
    ),
    children: [{ path: '404', element: <Page404 /> }],
  },

  // No match 404
  { path: '*', element: <Navigate to="/404" replace /> },
]);

export function AppRouter() {
  return <RouterProvider router={router} />;
}
